import { lazy } from 'react';
import {
  matchPath,
  Route,
  RouteProps,
  Routes,
  useLocation,
} from 'react-router';

import { NotFound } from '#/components/Status/NotFound';
import { HomePage } from '#/pages/Home/HomePage'; // landing page, not lazy
import { PageController } from '#/pages/PageController'; // landing page, not lazy
import { RedirectToDefaultSettings } from '#/pages/Settings/RedirectToDefaultSettings';
import { UnauthenticatedHomePage } from '#/pages/UnauthenticatedHomePage/UnauthenticatedHomePage'; // landing page, not lazy
import { useUnauthenticatedHomePage } from '#/pages/UnauthenticatedHomePage/UnauthenticatedHomePage.hook';
import { Path } from '#/service/routes';
import { useAuthStore } from '#/store/auth/auth.store';
import { playerActions } from '#/store/player/player.store';

import { useConfig } from './hooks/useConfig';
import { useDataContext } from './hooks/useDataContext';
import { useOnLocationChange } from './hooks/useOnLocationChange';

const DetailsPage = lazy(() =>
  import(/* webpackChunkName: "Details" */ '#/pages/Details/DetailsPage').then(
    (module) => ({ default: module.DetailsPage })
  )
);
const ProgramGuidePage = lazy(() =>
  import(
    /* webpackChunkName: "ProgramGuide" */ '#/pages/ProgramGuide/ProgramGuidePage'
  ).then((module) => ({ default: module.ProgramGuidePage }))
);
const PlaybackPage = lazy(() =>
  import(
    /* webpackChunkName: "Playback" */ '#/pages/Playback/PlaybackPage'
  ).then((module) => ({ default: module.PlaybackPage }))
);
const ReadMePage = lazy(() =>
  import(/* webpackChunkName: "ReadMe" */ '#/pages/ReadMe/ReadMePage').then(
    (module) => ({ default: module.ReadMePage })
  )
);
const TveSecondScreenAuthPage = lazy(() =>
  import(
    /* webpackChunkName: "Tve2ndScreenAuth" */ '#/pages/TveSecondScreenAuth/TveSecondScreenAuthPage'
  ).then((module) => ({ default: module.TveSecondScreenAuthPage }))
);
const SecondScreenAuthPage = lazy(() =>
  import(
    /* webpackChunkName: "2ndScreenAuth" */ '#/pages/SecondScreenAuth/SecondScreenAuthPage'
  ).then((module) => ({ default: module.SecondScreenAuthPage }))
);
const RegistrationPage = lazy(() =>
  import(
    /* webpackChunkName: "Registration" */ '#/pages/Registration/RegistrationPage'
  ).then((module) => ({ default: module.RegistrationPage }))
);
const LoginPage = lazy(() =>
  import(/* webpackChunkName: "Login" */ '#/pages/Login/LoginPage').then(
    (module) => ({ default: module.LoginPage })
  )
);
const SearchPage = lazy(() =>
  import(/* webpackChunkName: "Search" */ '#/pages/Search/SearchPage').then(
    (module) => ({ default: module.SearchPage })
  )
);
const SettingsPage = lazy(() =>
  import(
    /* webpackChunkName: "Settings" */ '#/pages/Settings/SettingsPage'
  ).then((module) => ({ default: module.SettingsPage }))
);
const SubscriptionPage = lazy(() =>
  import(
    /* webpackChunkName: "Subscription" */ '#/pages/Subscription/SubscriptionPage'
  ).then((module) => ({ default: module.SubscriptionPage }))
);
const WatchHistoryPage = lazy(() =>
  import(
    /* webpackChunkName: "WatchHistory" */ '#/pages/WatchHistory/WatchHistoryPage'
  ).then((module) => ({ default: module.WatchHistoryPage }))
);
const ForgotPasswordPage = lazy(() =>
  import(
    /* webpackChunkName: "ForgotPsw" */ '#/pages/ForgotPassword/ForgotPasswordPage'
  ).then((module) => ({ default: module.ForgotPasswordPage }))
);
const FavoritesPage = lazy(() =>
  import(
    /* webpackChunkName: "Favorites" */ '#/pages/Favorites/FavoritesPage'
  ).then((module) => ({ default: module.FavoritesPage }))
);
const ExternalPaymentPage = lazy(() =>
  import(
    /* webpackChunkName: "ExtPayment" */ '#/pages/ExternalPayment/ExternalPaymentPage'
  ).then((module) => ({ default: module.ExternalPaymentPage }))
);
const AlternativePurchasePage = lazy(() =>
  import(
    /* webpackChunkName: "AlternativePurchase" */ '#/pages/AlternativePurchase/AlternativePurchasePage'
  ).then((module) => ({ default: module.AlternativePurchasePage }))
);
const PurchaseGiftPage = lazy(() =>
  import(
    /* webpackChunkName: "PurchaseGift" */ '#/pages/Gifting/Purchase/PurchasePage'
  ).then((module) => ({ default: module.PurchasePage }))
);
const RedeemGiftPage = lazy(() =>
  import(
    /* webpackChunkName: "RedeemGift" */ '#/pages/Gifting/Redeem/RedeemPage'
  ).then((module) => ({ default: module.RedeemPage }))
);
const CancelPage = lazy(() =>
  import(/* webpackChunkName: "Cancel" */ '#/pages/Cancel/CancelPage').then(
    (module) => ({ default: module.CancelPage })
  )
);

const conditionalRoute = ({
  condition,
  ...routeProps
}: { condition: boolean } & RouteProps) =>
  condition ? <Route {...routeProps} /> : null;

export const AppRoutes = () => {
  const {
    isRegistrationEnabled,
    isForgotPasswordEnabled,
    isSecondScreen,
    isTve,
    isAuthEnabled,
  } = useAuthStore();
  const { featureConfig } = useConfig();
  const { shouldShow: shouldShowUnauthenticatedHomePage } =
    useUnauthenticatedHomePage();

  const defaultContext = useDataContext().getGeneralContext();

  const { pathname } = useLocation();

  // update itunes meta tag on navigation
  const { previousLocation } = useOnLocationChange(() => {
    const { href } = window.location;
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-itunes-app"]'
    );

    if (meta) {
      meta.content = meta.content.replace(/(app-argument=)[^,]+/, `$1${href}`);
    }
  });

  // reset the entered parental pin when navigating away from the playback page
  if (!matchPath(Path.Playback, pathname)) {
    playerActions.setEnteredParentalPin(null);
    playerActions.setEnteredParentalPinCount(0);
  }

  return (
    <Routes>
      {conditionalRoute({
        condition: isAuthEnabled,
        element: <LoginPage />,
        path: Path.Login,
      })}
      {conditionalRoute({
        condition: isRegistrationEnabled,
        element: <RegistrationPage goBackLocation={previousLocation} />,
        path: Path.Registration,
      })}
      {conditionalRoute({
        condition: isForgotPasswordEnabled,
        element: <ForgotPasswordPage isForgotPasswordPath />,
        path: Path.ForgotPassword,
      })}
      {conditionalRoute({
        condition: isForgotPasswordEnabled,
        element: <ForgotPasswordPage skipStep />,
        path: Path.UpdatePassword,
      })}
      {conditionalRoute({
        condition: !!featureConfig.epg?.enabled,
        element: <ProgramGuidePage />,
        path: Path.Channels,
      })}
      <Route element={<ReadMePage />} path={Path.Readme} />
      {conditionalRoute({
        condition: isTve && isSecondScreen,
        element: <TveSecondScreenAuthPage />,
        path: Path.TveSecondScreenLogin,
      })}
      {conditionalRoute({
        condition: isSecondScreen && !isTve,
        element: <SecondScreenAuthPage />,
        path: Path.SecondScreenLogin,
      })}
      <Route element={<SettingsPage />} path={Path.Settings} />
      <Route element={<RedirectToDefaultSettings />} path={Path.SettingsRoot} />
      <Route element={<SearchPage />} path={Path.Search} />
      <Route element={<WatchHistoryPage />} path={Path.History} />
      <Route element={<FavoritesPage />} path={Path.Favorites} />
      <Route element={<CancelPage />} path={Path.Cancel} />
      {conditionalRoute({
        condition: isAuthEnabled,
        element: <SubscriptionPage />,
        path: Path.Subscribe,
      })}
      <Route
        element={<PageController defaultContext={defaultContext} />}
        path={Path.Page}
      />
      <Route
        element={<DetailsPage key={location.pathname} />}
        path={Path.Details}
      />
      <Route
        /**
         * If you were wondering: `Hold on, what the heck is this?`, then let me explain what's going on here
         * Playback page for some reason is slightly strange and changing resource between playbacks
         * does not recreate component fully. Setting `key` to the pathname (which contains resourceId in this case)
         * enforces component to recreate between plays.
         * If you remove the `key` attribute, please test UpNext functionality
         */
        element={<PlaybackPage key={location.pathname} />}
        path={Path.Playback}
      />
      <Route element={<ExternalPaymentPage />} path={Path.External} />
      <Route
        element={<AlternativePurchasePage />}
        path={Path.AlternativePurchase}
      />
      <Route
        element={
          shouldShowUnauthenticatedHomePage ? (
            <UnauthenticatedHomePage />
          ) : (
            <HomePage />
          )
        }
        path={Path.Home}
      />
      {conditionalRoute({
        condition: !!featureConfig.purchase?.gifting?.enabled,
        element: <RedeemGiftPage />,
        path: Path.GiftRedeem,
      })}
      {conditionalRoute({
        condition: !!featureConfig.purchase?.gifting?.enabled,
        element: <PurchaseGiftPage />,
        path: Path.GiftPurchase,
      })}
      {/* everything else */}
      <Route element={<NotFound />} path="*" />
    </Routes>
  );
};
